/**
 * Minimal dependency-free CSV parser, deliberately LENIENT about quotes.
 *
 * Handles properly quoted fields (embedded commas and doubled quotes, e.g. "Smith, Jr." or
 * "She said ""hi""") and both \n and \r\n line endings.
 *
 * Two rules make it lenient, and both exist because of real exports:
 *
 * 1. A double-quote only opens a quoted field at the START of a field. Previously a quote
 *    ANYWHERE toggled quote mode, so a name like `ELECTRICAL ELECTRONIC CONTROL, INC. ("EE`
 *    — an unbalanced quote from a source system that truncated the name mid-word — put the
 *    parser into quoted mode and it consumed every following comma and NEWLINE as literal
 *    text until the next quote character appeared. In the first real client export that
 *    swallowed 6,251 rows into a single field, and 7,602 of 18,880 rows overall vanished with
 *    no error of any kind. Mid-field quotes are content (`DOUBLE "P" CONCRETE PUMPING`) and
 *    are now kept as such.
 *
 * 2. A quoted field whose closing quote is not followed by a comma, a newline, or end of
 *    input was never really a quoted field: `"JETS" JEWISH EDUCATIONAL TRADE SCHOOL` is one
 *    name that happens to begin with a quote, not the field `JETS` followed by garbage. Such
 *    a field is re-read literally.
 *
 * The strictly-correct alternative — rejecting the file — is worse: these exports arrive
 * nightly, unattended, and are the firm's only copy of the data. Losing rows silently is the
 * one outcome that must not happen.
 *
 * Returns { headers: string[], rows: string[][] } — rows are ARRAYS, positionally aligned to
 * `headers`. Callers that want named access must zip them (see feeds/csvRows.js).
 */
export function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  let fieldStart = true;   // are we at the first character of a field?
  let quoteOpenedAt = -1;  // index of the opening quote, so an unterminated one can be undone

  const pushField = () => {
    row.push(field);
    field = '';
    fieldStart = true;
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };

  const clean = String(text).replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  for (let i = 0; i < clean.length; i += 1) {
    const c = clean[i];
    if (inQuotes) {
      if (c === '"') {
        if (clean[i + 1] === '"') {
          field += '"';
          i += 1;
        } else if (clean[i + 1] === ',' || clean[i + 1] === '\n' || i + 1 >= clean.length) {
          // A real closing quote: the field ends here.
          inQuotes = false;
        } else {
          // Rule 2: the quotes were content, not delimiters. Re-read the field literally
          // from the opening quote so nothing is lost or silently re-interpreted.
          field = clean.slice(quoteOpenedAt, i + 1);
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"' && fieldStart) {
      // Rule 1: only a quote at the start of a field opens a quoted field.
      inQuotes = true;
      quoteOpenedAt = i;
      fieldStart = false;
    } else if (c === ',') {
      pushField();
    } else if (c === '\n') {
      pushRow();
    } else {
      field += c;
      fieldStart = false;
    }
  }
  // An unterminated quoted field: treat everything from the opening quote as literal text
  // rather than swallowing the rest of the file into one value.
  if (inQuotes && quoteOpenedAt >= 0) {
    const literal = clean.slice(quoteOpenedAt);
    const upToNewline = literal.indexOf('\n');
    field = upToNewline === -1 ? literal : literal.slice(0, upToNewline);
    // Anything after that newline is a fresh set of rows, so re-parse the remainder.
    if (upToNewline !== -1) {
      pushRow();
      const rest = parseCsv(literal.slice(upToNewline + 1));
      const restRows = rest.headers.length ? [rest.headers, ...rest.rows] : [];
      const nonEmptyHere = rows.filter((r) => r.some((cell) => cell.trim() !== ''));
      const [headers0, ...dataRows0] = nonEmptyHere;
      return { headers: headers0 || [], rows: [...dataRows0, ...restRows] };
    }
  }
  // Trailing field/row not yet flushed (files without a final newline).
  if (field.length > 0 || row.length > 0) pushRow();

  const nonEmpty = rows.filter((r) => r.some((cell) => cell.trim() !== ''));
  const [headers, ...dataRows] = nonEmpty;
  return { headers: headers || [], rows: dataRows };
}

/**
 * Converts "Last, First" / "Last, First, Middle" / "Last, First M." style names —
 * the format Aderant/SSRS exports use (e.g. "WILLIAMS, TRICIA, L.") — into
 * "First Last" order. Leaves already-"First Last"-shaped names (no comma) alone.
 * A trailing single-letter/initial segment (with or without a period) is dropped
 * rather than appended, since "Tricia L Williams" reads worse than "Tricia Williams"
 * for display purposes and the middle initial isn't used anywhere downstream.
 * Title-cases the result so all-caps SSRS exports ("WILLIAMS") become "Williams".
 */
export function normalizeName(raw) {
  const value = String(raw || '').trim();
  if (!value.includes(',')) return value;

  const parts = value
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2) return value;

  const [last, first, ...rest] = parts;
  // Drop a trailing bare middle initial ("L", "L.") — keep anything longer
  // (e.g. a real middle name) as part of the first-name segment. Handles both
  // "Last, First, L." (initial as its own segment) and "Last, First L."
  // (initial trailing the first-name segment with no comma before it).
  const middle = rest.find((p) => !/^[A-Za-z]\.?$/.test(p));
  const firstNoTrailingInitial = first.replace(/\s+[A-Za-z]\.?$/, '');

  const titleCase = (s) =>
    s
      .toLowerCase()
      .replace(/\b([a-z])/g, (m, c) => c.toUpperCase());

  const firstPart = middle ? `${firstNoTrailingInitial} ${middle}` : firstNoTrailingInitial;
  return `${titleCase(firstPart)} ${titleCase(last)}`.trim();
}

/**
 * True when an email's local-part (the segment before @) ends with "_FOW",
 * case-insensitively — the marker SSRS uses for role-placeholder rows (e.g.
 * RPA1_FOW@aalrr.com "RESPONSIBLE PARTNER", PGL1_FOW@aalrr.com "PRACTICE G.
 * LEADER") rather than a real employee. These rows must never be imported as
 * roster people and must never be used as a source for PGL derivation.
 */
export function isPlaceholderEmail(email) {
  const value = String(email || '').trim().toLowerCase();
  const at = value.indexOf('@');
  if (at === -1) return false;
  return value.slice(0, at).endsWith('_fow');
}

/**
 * Maps parsed CSV rows onto {name, email, employeeCode, practiceGroup, pgl,
 * pglName, isPlaceholderRow} objects by matching header names loosely
 * (case/space/underscore-insensitive) against common SSRS export column names,
 * so the admin doesn't need an exact header match. Names are normalized from
 * "Last, First" (Aderant/SSRS order) to "First Last".
 *
 * `employeeCode` carries the NMMExport CSV's EMPLOYEE_CODE column (short
 * internal employee code/initials, e.g. "DFH") -- distinct from `pgl`,
 * which is also initials-shaped but marks a practice-group leader rather
 * than identifying the employee.
 *
 * `pgl` (raw initials) and `pglName` (normalized display name) carry the
 * NMMExport CSV's PGL/PGLName columns, used to auto-derive each practice
 * group's leader. A literal "0" in either column (SSRS's way of saying "no
 * PGL", seen on MISCELLANEOUS-group rows) is treated as empty rather than
 * passed through literally. `isPlaceholderRow` flags SSRS's role-placeholder
 * marker rows (see isPlaceholderEmail) so callers can exclude them from both
 * roster import and PGL derivation.
 *
 * `isInactive` carries an optional "Inactive" column (matched loosely against
 * `inactive`/`isinactive`/`inactiveflag` headers): a trimmed, case-insensitive
 * "Y" (or "yes"/"true"/"1", accepted defensively even though the documented
 * contract is just Y/N) means the person should be imported as inactive; "N",
 * empty, or a missing column all mean active — `isInactive` is `false` in
 * every one of those cases. Callers should write `isActive: !isInactive`.
 */
export function mapResponsiblePartnerRows(headers, rows) {
  const norm = (s) => String(s || '').trim().toLowerCase().replace(/[\s_]+/g, '');
  const normalizedHeaders = headers.map(norm);

  const findIndex = (candidates) => {
    for (const candidate of candidates) {
      const idx = normalizedHeaders.indexOf(candidate);
      if (idx !== -1) return idx;
    }
    return -1;
  };

  // Repairs a row whose name cell contains UNQUOTED commas — the real NMMExport feed
  // writes support staff as "AGUILAR, ANA, B." with no quotes, splitting the name across
  // cells and shifting every later column. Applied only when the surplus provably sits
  // before the email column; see functions/src/lib/csv.js (the CommonJS mirror of this
  // file) for the full reasoning. Keep the two implementations identical.
  const repairUnquotedNameRow = (row, nameIdx, emailIdx) => {
    const extra = row.length - headers.length;
    if (extra <= 0 || nameIdx === -1 || emailIdx === -1 || nameIdx >= emailIdx) return row;
    const rowEmailIdx = row.findIndex((cell) => String(cell || '').includes('@'));
    if (rowEmailIdx !== emailIdx + extra) return row;
    return [
      ...row.slice(0, nameIdx),
      row.slice(nameIdx, nameIdx + extra + 1).join(','),
      ...row.slice(nameIdx + extra + 1),
    ];
  };

  const nameIdx = findIndex([
    'name',
    'employeename',
    'attorneyname',
    'partnername',
    'displayname',
    'fullname',
  ]);
  const emailIdx = findIndex(['email', 'emailaddress', 'attorneyemail']);
  const employeeCodeIdx = findIndex(['employeecode', 'empcode', 'employeeid', 'empid']);
  const groupIdx = findIndex([
    'practicegroupdescription',
    'practicegroup',
    'group',
    'pg',
    'department',
  ]);
  const pglIdx = findIndex(['pgl']);
  const pglNameIdx = findIndex(['pglname']);
  const inactiveIdx = findIndex(['inactive', 'isinactive', 'inactiveflag']);
  // AD_Title — see the functions/ mirror for why it wins over POSITION.
  const titleIdx = findIndex(['adtitle', 'title', 'jobtitle', 'position']);

  const cleanPglField = (raw) => {
    const value = String(raw || '').trim();
    return value === '0' ? '' : value;
  };

  // Y (and, defensively, yes/true/1) means inactive; N, empty, or a missing column
  // all mean active. Case/whitespace-insensitive per the column's documented contract.
  const parseInactiveFlag = (raw) => {
    const value = String(raw || '').trim().toLowerCase();
    return value === 'y' || value === 'yes' || value === 'true' || value === '1';
  };

  // The title column's own unquoted-comma case ("Partner, General Counsel") — see the
  // functions/ mirror for the full reasoning. Keep the two implementations identical.
  const titleFrom = (r) => {
    if (titleIdx === -1) return '';
    const extra = r.length - headers.length;
    if (
      extra > 0 &&
      titleIdx === headers.length - 1 &&
      emailIdx !== -1 &&
      r.findIndex((cell) => String(cell || '').includes('@')) === emailIdx
    ) {
      return r
        .slice(titleIdx)
        .map((cell) => String(cell || '').trim())
        .filter(Boolean)
        .join(', ');
    }
    return (r[titleIdx] || '').trim();
  };

  return rows.map((raw) => {
    const r = repairUnquotedNameRow(raw, nameIdx, emailIdx);
    const email = emailIdx !== -1 ? (r[emailIdx] || '').trim() : '';
    const rawPglName = pglNameIdx !== -1 ? cleanPglField(r[pglNameIdx]) : '';
    return {
      name: nameIdx !== -1 ? normalizeName(r[nameIdx]) : '',
      email,
      employeeCode: employeeCodeIdx !== -1 ? (r[employeeCodeIdx] || '').trim() : '',
      practiceGroup: groupIdx !== -1 ? (r[groupIdx] || '').trim() : '',
      pgl: pglIdx !== -1 ? cleanPglField(r[pglIdx]) : '',
      pglName: rawPglName ? normalizeName(rawPglName) : '',
      title: titleFrom(r),
      isPlaceholderRow: isPlaceholderEmail(email),
      isInactive: inactiveIdx !== -1 ? parseInactiveFlag(r[inactiveIdx]) : false,
    };
  });
}

/**
 * Deliberate client mirror of matchPracticeGroupText in
 * functions/src/admin/adminActions.js \u2014 keep both copies in sync. Used to preview,
 * before import, which CSV rows will link to an existing practice group vs. which
 * will import without one (the server applies the identical matching logic when the
 * import actually runs).
 *
 * @param {string} groupText - raw practice group text from the CSV row
 * @param {Array<{id: string, name?: string}>} groups - stored practice group records
 * @returns {string|null} matched practice group id, or null if no unambiguous match
 */
export function matchPracticeGroupText(groupText, groups) {
  const norm = String(groupText || '').trim().toLowerCase();
  if (!norm) return null;

  const named = groups
    .filter((g) => g.name)
    .map((g) => ({ norm: String(g.name).trim().toLowerCase(), id: g.id, name: g.name }));

  const exactByName = named.find((g) => g.norm === norm);
  if (exactByName) return exactByName.id;
  const exactById = groups.find((g) => String(g.id).trim().toLowerCase() === norm);
  if (exactById) return exactById.id;

  const prefixMatches = named.filter((g) => g.norm.startsWith(norm) || norm.startsWith(g.norm));
  if (prefixMatches.length === 1) return prefixMatches[0].id;
  return null;
}
